/**
 * Customer offboarding behind maker-checker: one platform admin asks, with a
 * reason and the blast radius recorded; a different one approves (and it runs)
 * or rejects. Soft deactivates and can be undone; hard erases, needs the
 * customer's name typed back and is refused while the ledger still points at
 * the rows it would erase.
 */
import { api, apiErrorMessage } from '@lipaflow/shared'

export type OffboardMode = 'soft' | 'hard'

export interface OffboardRequest {
  id: string
  tenant_id: string
  mode: OffboardMode
  reason: string
  status: 'pending' | 'approved' | 'rejected' | 'executed' | 'failed' | string
  /** Rows affected, by table. */
  blast_radius: Record<string, number>
  requested_by_user_id: string | null
  requested_at: string
  decided_by_user_id: string | null
  decided_at: string | null
  decision_note: string | null
  executed_at: string | null
  result: Record<string, unknown>
}

export const offboarding = {
  /** The sector's requests, newest first; `pending` is the inbox. */
  async list(sector: string, status?: string): Promise<OffboardRequest[]> {
    return (await api.get<OffboardRequest[]>('/admin/offboard-requests', {
      params: { vertical_code: sector, ...(status ? { status_filter: status } : {}) },
    })).data
  },
  async request(tenantId: string, body: { mode: OffboardMode; reason: string; confirm_tenant_name?: string }): Promise<OffboardRequest> {
    return (await api.post<OffboardRequest>(`/admin/tenants/${tenantId}/offboard`, body)).data
  },
  async approve(requestId: string, note?: string): Promise<OffboardRequest> {
    return (await api.post<OffboardRequest>(`/admin/offboard-requests/${requestId}/approve`, { note })).data
  },
  async reject(requestId: string, note?: string): Promise<OffboardRequest> {
    return (await api.post<OffboardRequest>(`/admin/offboard-requests/${requestId}/reject`, { note })).data
  },
}

/** The server's refusal, with the reasons it gives (hard mode's ledger blockers). */
export function offboardError(err: unknown, fallback: string): string {
  const detail = (err as { response?: { data?: { detail?: { blockers?: unknown } } } })?.response?.data?.detail
  const blockers = Array.isArray(detail?.blockers) ? (detail.blockers as unknown[]).map(String) : []
  const message = apiErrorMessage(err, fallback)
  return blockers.length ? `${message}: ${blockers.join('; ')}` : message
}
